import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import DashboardLayout from '../../components/layout/DashboardLayout';
import Input from '../../components/common/Input';
import Select from '../../components/common/Select';
import Button from '../../components/common/Button';
import { recipesApi } from '../../api/recipes.api';
import { uploadApi } from '../../api/upload.api';
import { getRecipeImageUrl } from '../../utils/images';
import { PlusIcon, XMarkIcon, PhotoIcon, ArrowUpTrayIcon } from '@heroicons/react/24/outline';

const instructionEnOptions = [
  { value: 'much', label: 'Much (uncountable)' },
  { value: 'many', label: 'Many (countable)' }
];

const instructionEsOptions = [
  { value: 'masc', label: 'Masculine' },
  { value: 'fem', label: 'Feminine' },
  { value: 'masc_unit', label: 'Masculine with Unit' },
  { value: 'fem_unit', label: 'Feminine with Unit' }
];

const emptyIngredient = {
  ingredient_one: '',
  ingredient_many: '',
  ingredient_es_one: '',
  ingredient_es_many: '',
  quantity: 1,
  unit: '',
  instruction_en: 'many',
  instruction_es: 'masc'
};

export default function RecipeFormPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const isEdit = !!id;

  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [selectedImage, setSelectedImage] = useState(null);
  const [imagePreview, setImagePreview] = useState(null);
  const [uploadingImage, setUploadingImage] = useState(false);
  const [imageCacheKey, setImageCacheKey] = useState(Date.now());
  const fileInputRef = useRef(null);

  const [formData, setFormData] = useState({
    name: '',
    name_es: '',
    ingredients: [],
    is_main: true,
    default: true,
    image: ''
  });

  useEffect(() => {
    if (isEdit) {
      setLoading(true);
      recipesApi.getById(id).then((res) => {
        const recipe = res.data;
        setFormData({
          name: recipe.name || '',
          name_es: recipe.name_es || '',
          ingredients: recipe.ingredients || [],
          is_main: recipe.is_main || false,
          default: recipe.default || false,
          image: recipe.image || ''
        });
        setLoading(false);
      }).catch(() => {
        setError('Failed to load recipe');
        setLoading(false);
      });
    }
  }, [id, isEdit]);

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
    setError('');
  };

  const handleIngredientChange = (index, field, value) => {
    setFormData((prev) => ({
      ...prev,
      ingredients: prev.ingredients.map((ing, i) =>
        i === index ? { ...ing, [field]: value } : ing
      )
    }));
  };

  const handleAddIngredient = () => {
    setFormData((prev) => ({
      ...prev,
      ingredients: [...prev.ingredients, { ...emptyIngredient }]
    }));
  };

  const handleRemoveIngredient = (index) => {
    setFormData((prev) => ({
      ...prev,
      ingredients: prev.ingredients.filter((_, i) => i !== index)
    }));
  };

  const handleImageSelect = (e) => {
    const file = e.target.files[0];
    if (file) {
      if (!file.type.startsWith('image/')) {
        setError('Please select an image file');
        return;
      }
      if (file.size > 5 * 1024 * 1024) {
        setError('Image size must be less than 5MB');
        return;
      }
      setSelectedImage(file);
      setImagePreview(URL.createObjectURL(file));
      setError('');
    }
  };

  const handleRemoveSelectedImage = () => {
    setSelectedImage(null);
    if (imagePreview) {
      URL.revokeObjectURL(imagePreview);
      setImagePreview(null);
    }
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    // Validate required fields
    if (!formData.name.trim()) {
      setError('Name (English) is required');
      return;
    }
    if (!formData.name_es.trim()) {
      setError('Name (Spanish) is required');
      return;
    }

    // Validate ingredients
    if (formData.ingredients.length === 0) {
      setError('At least one ingredient is required');
      return;
    }

    // Check each ingredient has required EN and ES fields
    for (let i = 0; i < formData.ingredients.length; i++) {
      const ing = formData.ingredients[i];
      if (!ing.ingredient_one?.trim() || !ing.ingredient_many?.trim()) {
        setError(`Ingredient #${i + 1}: English names (singular and plural) are required`);
        return;
      }
      if (!ing.ingredient_es_one?.trim() || !ing.ingredient_es_many?.trim()) {
        setError(`Ingredient #${i + 1}: Spanish names (singular and plural) are required`);
        return;
      }
    }

    setSaving(true);

    try {
      let imageName = formData.image;

      // Upload image if selected
      if (selectedImage) {
        setUploadingImage(true);
        try {
          // Generate image name from recipe name: lowercase, underscores, no special chars
          const imageFileName = formData.name
            .toLowerCase()
            .normalize('NFD')
            .replace(/[\u0300-\u036f]/g, '') // Remove accents
            .replace(/[^a-z0-9\s]/g, '') // Remove special characters
            .replace(/\s+/g, '_') // Replace spaces with underscores
            .trim() + '.png';
          await uploadApi.uploadRecipeImage(selectedImage, imageFileName);
          imageName = imageFileName;
          setImageCacheKey(Date.now());
        } catch (uploadErr) {
          console.error('Image upload failed:', uploadErr);
          setError('Image upload failed. Recipe will be saved without image.');
        }
        setUploadingImage(false);
      }

      const dataToSend = {
        ...formData,
        image: imageName
      };

      if (isEdit) {
        await recipesApi.update(id, dataToSend);
      } else {
        await recipesApi.create(dataToSend);
      }
      navigate('/recipes');
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to save recipe');
    } finally {
      setSaving(false);
    }
  };

  const baseImageUrl = getRecipeImageUrl(formData.image);
  const imageUrl = baseImageUrl ? `${baseImageUrl}?t=${imageCacheKey}` : null;

  if (loading) {
    return (
      <DashboardLayout>
        <div className="flex justify-center py-8">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600"></div>
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout>
      <div className="mb-6">
        <h1 className="text-2xl font-bold text-gray-900">
          {isEdit ? 'Edit Recipe' : 'Create Recipe'}
        </h1>
      </div>

      <div className="bg-white rounded-lg shadow p-6">
        {error && (
          <div className="mb-4 p-3 bg-red-50 border border-red-200 text-red-700 rounded-lg">
            {error}
          </div>
        )}

        <form onSubmit={handleSubmit} className="space-y-6">
          {/* Image Section */}
          <div className="border rounded-lg p-4 bg-gray-50">
            <label className="block text-sm font-medium text-gray-700 mb-3">
              Recipe Image
            </label>
            <div className="flex items-start gap-6">
              {/* Current Image */}
              <div>
                <p className="text-xs text-gray-500 mb-2">Current Image</p>
                <div className="h-32 w-32 rounded-lg overflow-hidden bg-white flex items-center justify-center border">
                  {imageUrl && !imagePreview ? (
                    <img
                      src={imageUrl}
                      alt={formData.name}
                      className="h-full w-full object-cover"
                      onError={(e) => {
                        e.target.style.display = 'none';
                        e.target.nextSibling.style.display = 'flex';
                      }}
                    />
                  ) : null}
                  <span className={`text-gray-400 text-sm ${imageUrl && !imagePreview ? 'hidden' : 'flex'} flex-col items-center justify-center h-full w-full`}>
                    <PhotoIcon className="h-8 w-8 mb-1" />
                    No image
                  </span>
                </div>
              </div>

              {/* New Image Preview */}
              {imagePreview && (
                <div>
                  <p className="text-xs text-gray-500 mb-2">New Image</p>
                  <div className="h-32 w-32 rounded-lg overflow-hidden bg-white flex items-center justify-center border-2 border-primary-500 relative">
                    <img
                      src={imagePreview}
                      alt="Preview"
                      className="h-full w-full object-cover"
                    />
                    <button
                      type="button"
                      onClick={handleRemoveSelectedImage}
                      className="absolute top-1 right-1 p-1 bg-red-500 text-white rounded-full hover:bg-red-600"
                    >
                      <XMarkIcon className="h-4 w-4" />
                    </button>
                  </div>
                  <p className="mt-1 text-xs text-green-600">Ready to upload</p>
                </div>
              )}

              {/* Upload Button */}
              <div className="flex flex-col justify-center">
                <input
                  ref={fileInputRef}
                  type="file"
                  accept="image/*"
                  onChange={handleImageSelect}
                  className="hidden"
                />
                <Button
                  type="button"
                  variant="secondary"
                  onClick={() => fileInputRef.current?.click()}
                >
                  <ArrowUpTrayIcon className="h-5 w-5 mr-2" />
                  {imagePreview ? 'Change Image' : 'Upload Image'}
                </Button>
                <p className="mt-2 text-xs text-gray-500">
                  Max size: 5MB<br />PNG, JPG, or GIF
                </p>
              </div>
            </div>
          </div>

          {/* Basic Info */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <Input
              label="Name (English) *"
              name="name"
              value={formData.name}
              onChange={handleChange}
              placeholder="e.g., Apple Pie"
              required
            />
            <Input
              label="Name (Spanish) *"
              name="name_es"
              value={formData.name_es}
              onChange={handleChange}
              placeholder="e.g., Pastel de Manzana"
              required
            />
          </div>

          {/* Checkboxes */}
          <div className="flex flex-wrap gap-6">
            <label className="flex items-center">
              <input
                type="checkbox"
                name="is_main"
                checked={formData.is_main}
                onChange={handleChange}
                className="h-4 w-4 text-primary-600 focus:ring-primary-500 border-gray-300 rounded"
              />
              <span className="ml-2 text-sm text-gray-900">Main Recipe</span>
            </label>
            <label className="flex items-center">
              <input
                type="checkbox"
                name="default"
                checked={formData.default}
                onChange={handleChange}
                className="h-4 w-4 text-primary-600 focus:ring-primary-500 border-gray-300 rounded"
              />
              <span className="ml-2 text-sm text-gray-900">Default</span>
            </label>
          </div>

          {/* Ingredients Section */}
          <div className="border rounded-lg p-4">
            <div className="flex justify-between items-center mb-4">
              <label className="block text-sm font-medium text-gray-700">
                Ingredients * ({formData.ingredients.length})
              </label>
              <Button type="button" variant="secondary" onClick={handleAddIngredient}>
                <PlusIcon className="h-5 w-5 mr-2" />
                Add Ingredient
              </Button>
            </div>

            {formData.ingredients.length === 0 ? (
              <p className="text-red-500 text-sm text-center py-4">
                At least one ingredient is required. Click "Add Ingredient" to start.
              </p>
            ) : (
              <div className="space-y-4">
                {formData.ingredients.map((ingredient, index) => (
                  <div key={index} className="border rounded-lg p-4 bg-gray-50 relative">
                    <button
                      type="button"
                      onClick={() => handleRemoveIngredient(index)}
                      className="absolute top-2 right-2 p-1 text-gray-400 hover:text-red-600"
                    >
                      <XMarkIcon className="h-5 w-5" />
                    </button>

                    <p className="text-sm font-medium text-gray-700 mb-3">
                      Ingredient #{index + 1}
                    </p>

                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                      <Input
                        label="English (Singular) *"
                        value={ingredient.ingredient_one}
                        onChange={(e) => handleIngredientChange(index, 'ingredient_one', e.target.value)}
                        placeholder="e.g., apple"
                        required
                      />
                      <Input
                        label="English (Plural) *"
                        value={ingredient.ingredient_many}
                        onChange={(e) => handleIngredientChange(index, 'ingredient_many', e.target.value)}
                        placeholder="e.g., apples"
                        required
                      />
                      <Input
                        label="Spanish (Singular) *"
                        value={ingredient.ingredient_es_one}
                        onChange={(e) => handleIngredientChange(index, 'ingredient_es_one', e.target.value)}
                        placeholder="e.g., manzana"
                        required
                      />
                      <Input
                        label="Spanish (Plural) *"
                        value={ingredient.ingredient_es_many}
                        onChange={(e) => handleIngredientChange(index, 'ingredient_es_many', e.target.value)}
                        placeholder="e.g., manzanas"
                        required
                      />
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mt-4">
                      <Input
                        label="Quantity"
                        type="number"
                        min="0"
                        step="0.1"
                        value={ingredient.quantity}
                        onChange={(e) => handleIngredientChange(index, 'quantity', parseFloat(e.target.value) || 0)}
                      />
                      <Input
                        label="Unit"
                        value={ingredient.unit}
                        onChange={(e) => handleIngredientChange(index, 'unit', e.target.value)}
                        placeholder="e.g., cups, pieces"
                      />
                      <Select
                        label="Instruction (EN)"
                        value={ingredient.instruction_en}
                        onChange={(e) => handleIngredientChange(index, 'instruction_en', e.target.value)}
                        options={instructionEnOptions}
                      />
                      <Select
                        label="Instruction (ES)"
                        value={ingredient.instruction_es}
                        onChange={(e) => handleIngredientChange(index, 'instruction_es', e.target.value)}
                        options={instructionEsOptions}
                      />
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>

          <div className="flex justify-end gap-4 pt-4 border-t">
            <Button type="button" variant="secondary" onClick={() => navigate('/recipes')}>
              Cancel
            </Button>
            <Button type="submit" loading={saving || uploadingImage}>
              {uploadingImage ? 'Uploading Image...' : (isEdit ? 'Update Recipe' : 'Create Recipe')}
            </Button>
          </div>
        </form>
      </div>
    </DashboardLayout>
  );
}
